import { Box, Stack, Typography } from "@mui/material"
import { DataGridPro } from "@mui/x-data-grid-pro"
import { coursesColumns } from "./coursesColumns"
import appTheme from "../../../theme/appTheme"
import { useAppSelector } from "@app/hooks"
import {
  selectCourseFilters,
  selectMainPageCourses,
} from "@features/search/searchSlice"
import { selectCoursesFetchingStatus } from "@features/coursesMenu/coursesSelectors"
import { LoadingComponent } from "@cmp/loaders/loadingComponent"
import { useNavigate } from "react-router-dom"

export function CoursesTable() {
  const navigate = useNavigate()
  const latestVersionCourses = useAppSelector(selectMainPageCourses)

  const onOpenClicked = (courseId: string) => {
    navigate(`/course-versions/${courseId}`)
  }

  return (
    <Box
      sx={{
        maxWidth: "1240px",
        maxHeight: "100%",
        bgcolor: "eTypes.sand15",
      }}
    >
      <DataGridPro
        rows={latestVersionCourses}
        columns={coursesColumns}
        disableColumnMenu
        disableRowSelectionOnClick
        disableColumnReorder
        rowHeight={78}
        slots={{ noRowsOverlay: CoursesNoRowsOverlay }}
        onRowClick={(gridRowParams) => {
          const courseId = gridRowParams?.row?.id || ""
          onOpenClicked(courseId)
        }}
        sx={{
          border: "none",
          "& .MuiDataGrid-row": {
            backgroundColor: "white",
            "&:hover": {
              cursor: "pointer",
            },
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: appTheme.palette.action.hover,
          },
          "& .MuiDataGrid-cell": {
            p: "0px 0px 0px 16px !important",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeader:focus": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnHeader": {
            p: "0px 0px 0px 16px !important",
          },
          "& .MuiDataGrid-columnHeader:focus-within": {
            outline: "none !important",
          },
          "& .MuiDataGrid-footerContainer": {
            display: "none",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
        }}
      />
    </Box>
  )
}

// Honest zero state: tell apart "still loading" and "nothing matches this
// search/filter/tab" instead of a bare "No rows" (the error state already
// renders in the page header).
function CoursesNoRowsOverlay() {
  const fetchingStatus = useAppSelector(selectCoursesFetchingStatus)
  const { text, advanceFilters } = useAppSelector(selectCourseFilters)
  const hasActiveFilters = advanceFilters.some(
    (filter) => filter.selectedValues.length > 0,
  )

  if (fetchingStatus === "loading") {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <LoadingComponent loadedComponents="courses" />
      </Stack>
    )
  }

  if (fetchingStatus === "error") {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <Typography variant="body1_sb">
          Courses couldn't be loaded, so there is nothing to show.
        </Typography>
      </Stack>
    )
  }

  const message = text
    ? `No courses match "${text}"${
        hasActiveFilters ? " with the active filters" : ""
      }`
    : hasActiveFilters
    ? "No courses match the active filters"
    : "No courses in this view"

  return (
    <Stack height="100%" alignItems="center" justifyContent="center" gap={0.5}>
      <Typography variant="body1_sb">{message}</Typography>
      {(text || hasActiveFilters) && (
        <Typography variant="body2" color="text.secondary">
          Try a different search or remove a filter above.
        </Typography>
      )}
    </Stack>
  )
}
